import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { hasMinimumRole, canEditAssociation } from '../../hartUtils';
import { ASSOCIATION_CONFIGS } from './config';
import { useAssociationData } from './hooks/useAssociationData';
import AssociationAccordion from './AssociationAccordion';

const AssociationsSection = ({
  parentId,
  parentData = {},
  parentType,
  associations = [],
  editable = false,
  editableAssociationCallbacks = {},
}) => {
  const { projectId } = useParams();
  const [editableAssignments, setEditableAssignments] = useState({});
  const [hasInitialized, setHasInitialized] = useState(false);
  const [currentParentId, setCurrentParentId] = useState(parentId);

  // Build full association configs from the simple list, filtering by role if needed
  const associationConfigs = useMemo(() => {
    if (!parentType) {
      console.warn('AssociationsSection: parentType is required');
      return [];
    }

    return associations
      .map(key => {
        const config = ASSOCIATION_CONFIGS[parentType]?.[key];
        if (!config) {
          console.warn(`Unknown association key: ${key} for parent type: ${parentType}. Skipping.`);
          return null;
        }

        // Check if user has required role level for this association
        if (config.requiredRole && projectId) {
          if (!hasMinimumRole(config.requiredRole, projectId)) {
            return null; // User doesn't have required role, hide this association
          }
        }

        return { ...config, key };
      })
      .filter(Boolean);
  }, [associations, parentType, projectId]);

  const { data, loading, errors } = useAssociationData(parentId, parentData, associationConfigs);

  // Reset state when leaving edit mode or switching parent record
  useEffect(() => {
    if (!editable) {
      setHasInitialized(false);
      setEditableAssignments({});
    }

    if (parentId !== currentParentId) {
      setCurrentParentId(parentId);
      setHasInitialized(false);
      setEditableAssignments({});
    }
  }, [editable, parentId, currentParentId]);


  // Initialize editable assignments from data state (what's displayed in table) when entering edit mode
  useEffect(() => {
    if (!editable || !parentType) return;

    if (hasInitialized) return;

    const newAssignments = {};

    associationConfigs.forEach(assoc => {
      if (!assoc.isEditable) return;

      const idField = assoc.idField || 'id'; // Default to 'id' if not specified
      // Use data state (what's displayed in table) instead of parentData directly
      const assignmentData = data[assoc.key] || [];

      if (Array.isArray(assignmentData) && assignmentData.length > 0) {
        newAssignments[assoc.key] = assignmentData.map((a, i) => {
          if (a && typeof a === 'object') {
            return {
              ...a,
              _key: a[idField] || `existing-${i}`,
            };
          }

          // If the assignment is a primitive (e.g., just an id), wrap it in an object
          return {
            [idField]: a,
            _key: `existing-${i}`,
          };
        });
      } else {
        // Initialize as empty array if not present
        newAssignments[assoc.key] = [];
      }
    });

    setEditableAssignments(newAssignments);
    setHasInitialized(true);
  }, [editable, parentType, data, associationConfigs, hasInitialized]);

  // Generic handler for editable association changes
  const handleEditableChange = (associationKey, newAssignments) => {
    setEditableAssignments(prev => ({
      ...prev,
      [associationKey]: newAssignments,
    }));

    // Call parent callback if provided
    const callback = editableAssociationCallbacks[associationKey];
    if (callback) {
      const clean = newAssignments.map(({ _key, ...rest }) => rest);
      callback(clean);
    }
  };

  if (associationConfigs.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h3" gutterBottom sx={{ mb: 2 }}>
        Associations
      </Typography>
      {associationConfigs.map((config) => {
        const associationEditable = editable && canEditAssociation(config.key, parentType, projectId);
        return (
          <AssociationAccordion
            key={config.key}
            config={config}
            data={data[config.key]}
            loading={loading[config.key]}
            error={errors[config.key]}
            parentId={parentId}
            parentType={parentType}
            projectId={projectId}
            editable={associationEditable}
            assignment={editableAssignments[config.key]}
            onChange={(val) => handleEditableChange(config.key, val)}
          />
        );
      })}
    </Box>
  );
};

AssociationsSection.propTypes = {
  parentId: PropTypes.string.isRequired,
  parentData: PropTypes.object,
  parentType: PropTypes.string.isRequired,
  associations: PropTypes.arrayOf(PropTypes.string).isRequired,
  editable: PropTypes.bool,
  editableAssociationCallbacks: PropTypes.object,
};

export default AssociationsSection;
